import {
    createSketchDimensionHistoryPatch,
} from '../../history/dimension/SketchDimensionHistoryAdapter';
import type {EntityPatch} from '../../history/types';
import {
    createExistingHistorySnapshot,
    createMissingHistorySnapshot,
} from '../../history/types';
import type {Viewer} from '../../viewer/Viewer';
import type {
    SketchDimensionTopologyChange,
    SketchDimensionTopologyChangePort,
} from '../../../types/sketch/dimension/SketchDimensionTopologyChangeTypes';
import type {SketchTrimDimensionStateSnapshot} from '../../../types/sketch/trim/SketchTrimDimensionIntegrationTypes';
import {SketchDimensionTopologyChangeService} from '../dimension/application/SketchDimensionTopologyChangeService';

/**
 * Trim 与智能尺寸领域的集成服务。
 *
 * 职责：
 * 1. 为当前活动草图捕获、比较和恢复尺寸仓库状态；
 * 2. 把 Trim 输出的持久拓扑变化交给通用尺寸拓扑服务；
 * 3. 构造与草图运行时快照同事务提交的尺寸历史补丁；
 * 4. 在仓库变化后统一刷新尺寸关系视图、测量值和标注布局。
 */
export class SketchTrimDimensionIntegrationService {
    /** Viewer 统一外观入口。 */
    private readonly viewer: Viewer;
    /** 通用尺寸拓扑变化服务。 */
    private readonly topologyChangeService: SketchDimensionTopologyChangePort;

    /**
     * @param viewer Viewer 统一外观入口。
     * @param topologyChangeService 尺寸拓扑迁移端口实现。
     */
    public constructor(
        viewer: Viewer,
        topologyChangeService: SketchDimensionTopologyChangePort = new SketchDimensionTopologyChangeService(),
    ) {
        this.viewer = viewer;
        this.topologyChangeService = topologyChangeService;
    }

    /**
     * 捕获当前活动草图的尺寸仓库状态。
     *
     * @returns 尺寸状态；缺少活动草图或尺寸仓库时返回 null。
     */
    public captureActiveState(): SketchTrimDimensionStateSnapshot | null {
        const repository = this.viewer.getSketchDimensionRepository?.() ?? null;
        const sketchId = this.viewer.sketchGroupManager?.activeSketchGroup?.persistentId ?? null;

        if (!repository || !sketchId) {
            return null;
        }

        return {
            sketchId,
            dimensions: repository.listBySketch(sketchId)
                .map((dimension) => dimension.toSnapshot()),
        };
    }

    /**
     * 应用裁剪产生的尺寸拓扑变化并刷新运行时关系视图。
     *
     * @param changes 持久拓扑变化列表。
     */
    public applyTopologyChanges(changes: readonly SketchDimensionTopologyChange[]): void {
        const repository = this.viewer.getSketchDimensionRepository?.() ?? null;
        const sketchId = this.viewer.sketchGroupManager?.activeSketchGroup?.persistentId ?? null;

        if (!repository || !sketchId) {
            return;
        }

        // 仓库变更必须先完成，再统一重建尺寸关系视图，避免关系短暂指向已删除对象。
        this.topologyChangeService.apply(repository, sketchId, changes);
        this.viewer.refreshSketchDimensionsAfterTopologyChange?.(sketchId);
    }

    /**
     * 根据尺寸 before/after 状态创建确定性历史补丁。
     *
     * @param before 变更前尺寸状态。
     * @param after 变更后尺寸状态。
     * @returns 尺寸领域历史补丁。
     */
    public createHistoryPatches(
        before: SketchTrimDimensionStateSnapshot | null,
        after: SketchTrimDimensionStateSnapshot | null,
    ): EntityPatch[] {
        if (!before || !after || before.sketchId !== after.sketchId) {
            return [];
        }

        const beforeById = new Map(before.dimensions.map((snapshot) => [snapshot.dimensionId, snapshot]));
        const afterById = new Map(after.dimensions.map((snapshot) => [snapshot.dimensionId, snapshot]));
        const dimensionIds = Array.from(new Set([
            ...beforeById.keys(),
            ...afterById.keys(),
        ])).sort((left, right) => left.localeCompare(right));
        const patches: EntityPatch[] = [];

        dimensionIds.forEach((dimensionId) => {
            const beforeSnapshot = beforeById.get(dimensionId) ?? null;
            const afterSnapshot = afterById.get(dimensionId) ?? null;

            if (JSON.stringify(beforeSnapshot) === JSON.stringify(afterSnapshot)) {
                return;
            }

            patches.push(createSketchDimensionHistoryPatch({
                entityId: dimensionId,
                operation: beforeSnapshot
                    ? (afterSnapshot ? 'update' : 'delete')
                    : 'create',
                before: beforeSnapshot
                    ? createExistingHistorySnapshot(beforeSnapshot)
                    : createMissingHistorySnapshot(),
                after: afterSnapshot
                    ? createExistingHistorySnapshot(afterSnapshot)
                    : createMissingHistorySnapshot(),
                metadata: {sketchId: before.sketchId},
            }));
        });

        return patches;
    }

    /**
     * 在当前仓库仍等于 expected 时恢复 target 状态。
     *
     * 该守卫用于异步历史提交失败，防止旧事务回滚覆盖用户后续的新编辑。
     *
     * @param expected 允许回滚的当前状态。
     * @param target 目标恢复状态。
     */
    public restoreStateIfCurrent(
        expected: SketchTrimDimensionStateSnapshot | null,
        target: SketchTrimDimensionStateSnapshot | null,
    ): void {
        if (!expected || !target || expected.sketchId !== target.sketchId) {
            return;
        }

        const current = this.captureStateBySketchId(expected.sketchId);

        if (!current || JSON.stringify(current.dimensions) !== JSON.stringify(expected.dimensions)) {
            return;
        }

        this.restoreState(target);
    }

    /**
     * 无条件恢复指定草图的尺寸状态。
     *
     * @param state 目标尺寸状态。
     */
    public restoreState(state: SketchTrimDimensionStateSnapshot | null): void {
        if (!state) {
            return;
        }

        const repository = this.viewer.getSketchDimensionRepository?.() ?? null;
        if (!repository) {
            return;
        }

        // 先移除当前桶中的全部条目，再按稳定顺序灌回目标快照，仓库事件负责同步渲染订阅。
        repository.listBySketch(state.sketchId).forEach((dimension) => {
            repository.removeDimensionForRestore(dimension.dimensionId);
        });
        state.dimensions.forEach((snapshot) => {
            repository.restoreDimension(snapshot);
        });
        this.viewer.refreshSketchDimensionsAfterTopologyChange?.(state.sketchId);
    }

    /**
     * 捕获指定草图的尺寸状态。
     *
     * @param sketchId 草图持久 ID。
     * @returns 尺寸状态；仓库缺失时返回 null。
     */
    private captureStateBySketchId(sketchId: string): SketchTrimDimensionStateSnapshot | null {
        const repository = this.viewer.getSketchDimensionRepository?.() ?? null;

        if (!repository) {
            return null;
        }

        return {
            sketchId,
            dimensions: repository.listBySketch(sketchId)
                .map((dimension) => dimension.toSnapshot()),
        };
    }
}
